import { useMemo } from 'react';
import type { ListingDesignItem } from '@/features/plp/domain/models/ProductListing';
import type { UsePlpListingReturn } from '@/features/plp/presentation/hooks/usePlpListing';
import { usePlpColumns } from '@/features/plp/presentation/hooks/usePlpColumns';
import { PlpHeader } from '@/features/plp/presentation/components/PlpHeader';
import { PlpGrid } from '@/features/plp/presentation/components/PlpGrid';
import { buildPlpCells } from '@/features/plp/presentation/components/plpCells.util';
import { PLP_BROWSE_PAGE_MAX_CLASS } from '@/features/plp/constants';
import { CategoryFilter } from './CategoryFilter';
import { RecentlyViewedEmpty } from './RecentlyViewedEmpty';
import { RecentlyViewedSkeleton } from './RecentlyViewedSkeleton';
import { RECENTLY_VIEWED_HEADER } from '../../infra/mappers/browsingHistory.mapper';

const recentlyViewedHeaderProps = {
  title: RECENTLY_VIEWED_HEADER,
  showDesktopBanner: false as const,
};

export interface RecentlyViewedListingShellProps extends UsePlpListingReturn {
  categories: string[];
  activeCategory: string;
  onCategoryClick: (category: string) => void;
  pincode?: string;
  openPdpInNewTab?: boolean;
  onProductClick?: (
    product: ListingDesignItem,
    scrollDepth?: number,
    horizontalPosition?: number
  ) => void;
  onWishlistClick?: (product: ListingDesignItem, productPosition: number) => void;
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
  onVideoClick?: (product: ListingDesignItem, productPosition: number) => void;
  onVideoCallCartAdd?: (product: ListingDesignItem) => void;
  onViewDetailsClick?: (product: ListingDesignItem) => void;
  onProductViewed?: (params: {
    product: ListingDesignItem;
    scrollDepth: number;
    horizontalPosition: number;
  }) => void;
  viewedProductIds?: Set<number>;
  shouldPauseInfiniteScroll?: () => boolean;
}

export function RecentlyViewedListingShell({
  listingMeta,
  allDesigns,
  hasMore,
  isLoading,
  isLoadingMore,
  isError,
  loadMore,
  onBeforeNavigate,
  sentinelRef,
  mobileRestoreDesignId,
  mobileScrollRestoreOffset,
  onMobileScrollRestoreComplete,
  categories,
  activeCategory,
  onCategoryClick,
  pincode,
  openPdpInNewTab = false,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onVideoCallCartAdd,
  onViewDetailsClick,
  onProductViewed,
  viewedProductIds,
  shouldPauseInfiniteScroll,
}: RecentlyViewedListingShellProps) {
  const columnCount = usePlpColumns();
  const cells = useMemo(
    () => buildPlpCells(allDesigns, columnCount, false, false, false),
    [allDesigns, columnCount],
  );

  const totalCount = listingMeta?.totalCount ?? 0;
  const isInitialLoading = isLoading && allDesigns.length === 0;
  const isEmpty = !isLoading && !isError && totalCount === 0;

  if (isInitialLoading) {
    return (
      <div className="flex min-h-[calc(100vh-56px)] flex-col overflow-visible">
        <div className={PLP_BROWSE_PAGE_MAX_CLASS}>
          <PlpHeader {...recentlyViewedHeaderProps} />
        </div>
        <RecentlyViewedSkeleton />
      </div>
    );
  }

  if (isEmpty) {
    return <RecentlyViewedEmpty />;
  }

  return (
    <div className="flex min-h-[calc(100vh-56px)] flex-col overflow-visible">
      <div className={PLP_BROWSE_PAGE_MAX_CLASS}>
        <PlpHeader
          {...recentlyViewedHeaderProps}
          totalCount={totalCount}
          countLabel="Designs"
        />
        {categories.length > 2 ? (
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryClick={onCategoryClick}
          />
        ) : null}
      </div>

      <div className={`${PLP_BROWSE_PAGE_MAX_CLASS} px-4 pb-8 pt-4`}>
        <PlpGrid
          cells={cells}
          columnCount={columnCount}
          onBeforeNavigate={onBeforeNavigate}
          sentinelRef={sentinelRef}
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          openPdpInNewTab={openPdpInNewTab}
          pincode={pincode}
          onProductClick={onProductClick}
          onWishlistClick={onWishlistClick}
          onWishlistToggle={onWishlistToggle}
          onVideoClick={onVideoClick}
          onVideoCallCartAdd={onVideoCallCartAdd}
          onViewDetailsClick={onViewDetailsClick}
          onProductViewed={onProductViewed}
          viewedProductIds={viewedProductIds}
          mobileRestoreDesignId={mobileRestoreDesignId}
          mobileScrollRestoreOffset={mobileScrollRestoreOffset}
          onMobileScrollRestoreComplete={onMobileScrollRestoreComplete}
          shouldPauseInfiniteScroll={shouldPauseInfiniteScroll}
        />
      </div>
    </div>
  );
}
